import React, { useRef, useState } from 'react';
import { Box, Popover } from '@mui/material';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TuneIcon from '@mui/icons-material/Tune';
import SpeedIcon from '@mui/icons-material/Speed';
import { LoadCustomProcessor } from 'new/components/flows/shared/customProcessor';
import { TestPanel } from 'new/components/flows/types/byo/v2/TestPanel/TestPanel';
import type { ByoCodeMode } from 'new/components/flows/types/byo/core/model';
import { StudioCodeEditor } from './StudioCodeEditor';
import { PackagesTable } from './PackagesTable';
import { RuntimeVariablesPanel } from './RuntimeVariablesPanel';
import { PerformanceConfigPanel } from './PerformanceConfigPanel';
import type { BYOPerformanceConfig } from 'new/components/flows/types/byo/core/model';
import { isRuntimeVarInvalid, type RuntimeVar } from './useStudioDraft';
import classes from './studio.module.scss';

export interface StudioCodeStepProps {
  code: string;
  onCodeChange: (code: string) => void;
  codeMode: ByoCodeMode;
  onCodeModeChange: (m: ByoCodeMode) => void;
  predefinedId: number | string | null;
  onPredefinedSelected: (id: number | string | null) => void;
  packages: string[];
  onPackagesChange: (p: string[]) => void;
  runtimeVars: RuntimeVar[];
  onRuntimeVarsChange: (v: RuntimeVar[]) => void;
  performanceConfig: BYOPerformanceConfig;
  onPerformanceConfigChange: (next: BYOPerformanceConfig) => void;
  testCode: any;
  /** Passed through unchanged to TestPanel's probe payload. */
  sourceConfig?: any;
  credentialId: number;
  folderPath?: string | null;
}

/**
 * Code step — "Define Processing Logic" (matches the design): a toolbar with
 * Packages / Variables popovers + "Save as a Reusable Connector Processor",
 * function tabs (Use Existing / Provide Your Own), and — for own code — a
 * side-by-side editor ({@link StudioCodeEditor}, with its Docs drawer +
 * fullscreen) and {@link TestPanel}. Reuses the existing pieces throughout.
 */
export function StudioCodeStep(props: StudioCodeStepProps) {
  const {
    code,
    onCodeChange,
    codeMode,
    onCodeModeChange,
    predefinedId,
    onPredefinedSelected,
    packages,
    onPackagesChange,
    runtimeVars,
    onRuntimeVarsChange,
    performanceConfig,
    onPerformanceConfigChange,
    testCode,
    sourceConfig,
    credentialId,
    folderPath,
  } = props;

  const showPerfConfig = useFeatureFlag('UI_BYO_PERFORMANCE_CONFIG');

  const [pkgAnchor, setPkgAnchor] = useState<HTMLElement | null>(null);
  const [varAnchor, setVarAnchor] = useState<HTMLElement | null>(null);
  const [perfAnchor, setPerfAnchor] = useState<HTMLElement | null>(null);

  // Owned here (not inside PackagesTable) so this memory survives the
  // Packages popover unmounting its content on every close — confirmed live
  // 2026-08-20: clicking back into the code editor closes the popover via
  // MUI's click-away, and MUI unmounts a closed Popover's children by
  // default, so a ref living inside PackagesTable itself was wiped on every
  // reopen. StudioCodeStep never unmounts across that cycle, so refs here do.
  const pkgDismissedRef = useRef<Set<string>>(new Set());
  const pkgUserEditedRef = useRef(false);

  const isExisting = codeMode === 'predefined';
  const pkgCount = packages.filter(Boolean).length;
  const varCount = runtimeVars.filter((v) => v.key.trim()).length;
  // Run used to proceed even with a visibly-invalid runtime variable (e.g. a
  // Number field left empty or non-numeric) — the Variables panel showed an
  // error but nothing downstream ever checked it. Block Run the same way an
  // unpicked file or unwritten code already does.
  const invalidVarCount = runtimeVars.filter(isRuntimeVarInvalid).length;

  const headerButtons = (
    <>
      <button
        type="button"
        className={`${classes.headerBtn} ${pkgAnchor ? classes.headerBtnActive : ''}`}
        onClick={(e) => setPkgAnchor(e.currentTarget)}
        data-testid="byo-studio-packages-btn"
      >
        <Inventory2OutlinedIcon sx={{ fontSize: 15 }} />
        Packages
        {pkgCount > 0 && <span className={classes.toolbarBadge}>{pkgCount}</span>}
      </button>
      <button
        type="button"
        className={`${classes.headerBtn} ${varAnchor ? classes.headerBtnActive : ''}`}
        onClick={(e) => setVarAnchor(e.currentTarget)}
        data-testid="byo-studio-vars-btn"
      >
        <TuneIcon sx={{ fontSize: 15 }} />
        Variables
        {varCount > 0 && <span className={classes.toolbarBadge}>{varCount}</span>}
      </button>
      {showPerfConfig && (
        <button
          type="button"
          className={`${classes.headerBtn} ${perfAnchor ? classes.headerBtnActive : ''}`}
          onClick={(e) => setPerfAnchor(e.currentTarget)}
          data-testid="byo-studio-perf-btn"
        >
          <SpeedIcon sx={{ fontSize: 15 }} />
          Performance
        </button>
      )}
    </>
  );

  return (
    <Box className={classes.defineCard} data-testid="byo-studio-code">
      {/* Function tabs */}
      <Box className={classes.funcTabs}>
        <button
          type="button"
          className={`${classes.funcTab} ${isExisting ? classes.funcTabActive : ''}`}
          onClick={() => onCodeModeChange('predefined')}
          data-testid="byo-studio-func-existing"
        >
          Use Existing Function
        </button>
        <button
          type="button"
          className={`${classes.funcTab} ${!isExisting ? classes.funcTabActive : ''}`}
          onClick={() => onCodeModeChange('custom')}
          data-testid="byo-studio-func-own"
        >
          Provide Your Own Function
        </button>
      </Box>

      <Box className={classes.codeWorkbench}>
        <Box className={classes.codeWorkbenchEditor}>
          {/* Both panels stay mounted, toggled by display — switching tabs
              used to fully unmount/remount LoadCustomProcessor, tearing down
              SearchControls' in-flight fetch (and the `setListingTransformType`
              mount effect) every time. That's why the list showed empty on
              the first switch and only "caught up" after 2-3 rapid
              switches — each switch raced a fresh fetch against another
              teardown. Confirmed bug, fixed 2026-08-20.

              `flex: 1, minHeight: 0` on the visible branch is NOT decoration —
              without it this wrapper is a plain `display:flex`/`block` box
              with the browser's flex-item default of `flex: 0 1 auto`, so it
              sizes to its CONTENT height instead of stretching to fill
              `.codeWorkbenchEditor` (a flex column). `.codeEditor` /
              `.existingPicker` (this wrapper's children) both assume THEY are
              handed a real height via `flex:1;min-height:0` — with no
              height to inherit, that chain collapses to ~0px and the Monaco
              editor renders into a sliver, indistinguishable from blank.
              Confirmed live 2026-08-21 (measured `.editorPane` at 5px tall on
              the real dev server) — this wrapper Box is the break in the
              chain, added by the fix above without carrying its flex sizing. */}
          <Box
            sx={{
              display: isExisting ? 'flex' : 'none',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }}
          >
            <LoadCustomProcessor
              inline
              sourceConfig={{ customProcessorId: predefinedId }}
              onSourceConfigChanged={(cfg: any) =>
                onPredefinedSelected(cfg?.customProcessorId ?? null)
              }
              onLoadAsTemplate={(loadedCode, loadedPackages) => {
                // Copies the picked processor's code as an editable starting
                // point, distinct from picking it by reference (the radio
                // select above) — switches to "Provide Your Own Function" so
                // the copied code is immediately visible and editable.
                onCodeChange(loadedCode);
                onPackagesChange(loadedPackages);
                onCodeModeChange('custom');
              }}
            />
          </Box>
          <Box
            sx={{
              display: isExisting ? 'none' : 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }}
          >
            <StudioCodeEditor
              code={code}
              onCodeChange={onCodeChange}
              mode={codeMode}
              onModeChange={onCodeModeChange}
              predefinedId={predefinedId}
              onPredefinedSelected={onPredefinedSelected}
              showModeToggle={false}
              extraHeaderButtons={headerButtons}
            />
          </Box>
        </Box>
        <Box className={classes.codeWorkbenchTest}>
          <TestPanel
            code={testCode}
            credentialId={credentialId}
            folderPath={folderPath}
            sourceConfig={sourceConfig ?? {}}
            filesOnly
            hideCopyPath
            externalDisabledReason={
              // A picked function runs with its own variables, not these.
              !isExisting && invalidVarCount > 0
                ? `Fix ${invalidVarCount > 1 ? `${invalidVarCount} invalid variables` : 'the invalid variable'} first`
                : null
            }
          />
        </Box>
      </Box>

      {/* Packages popover */}
      <Popover
        open={!!pkgAnchor}
        anchorEl={pkgAnchor}
        onClose={() => setPkgAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { className: classes.popoverPaper } }}
      >
        <PackagesTable
          value={packages}
          onChange={onPackagesChange}
          code={code}
          dismissedRef={pkgDismissedRef}
          userEditedRef={pkgUserEditedRef}
        />
      </Popover>

      {/* Variables popover */}
      <Popover
        open={!!varAnchor}
        anchorEl={varAnchor}
        onClose={() => setVarAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { className: classes.popoverPaper } }}
      >
        <RuntimeVariablesPanel value={runtimeVars} onChange={onRuntimeVarsChange} />
      </Popover>

      {/* Performance popover — gated by UI_BYO_PERFORMANCE_CONFIG flag */}
      {showPerfConfig && (
        <Popover
          open={!!perfAnchor}
          anchorEl={perfAnchor}
          onClose={() => setPerfAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { className: classes.popoverPaper } }}
        >
          <PerformanceConfigPanel value={performanceConfig} onChange={onPerformanceConfigChange} />
        </Popover>
      )}
    </Box>
  );
}

export default StudioCodeStep;
